import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { useSoundPacks } from '../context/SoundPacksContext';
import { DatabaseUserModel } from '../services/DatabaseUser';
import { DatabaseSoundPack, DatabaseSoundPackModel } from '../services/DatabaseSoundPack';
import { createStripeCartCheckoutSession } from '../services/stripe/stripeApi';
import { getStripe } from '../services/stripe/stripeClient';
import Button from './ui/Button';
import Divider from './ui/Divider';
import Headline from './ui/Headline';
import PackArtwork from './ui/PackArtwork';
import PackScroller from './ui/PackScroller';
import DotHexagon from './ui/DotHexagon';
import AllAccessBanner from './ui/AllAccessBanner';
import CartToggleButton from './CartToggleButton';
import { PackRemoveIcon } from '../constants/icons';
import { LANDING_PACKS_URL } from '../constants/urls';

// The Cart page: one narrow 620px column at every breakpoint, flat sections
// split by hairlines — no summary card, no sticky bar. Cart data, Stripe
// checkout and the Browse Packs upsell filter are unchanged from the earlier
// layout; this is a restyle.

function formatUSD(amount: number): string {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

function CartPage() {
    const navigate = useNavigate();
    const { user, userData, isLoading: userLoading } = useUser();
    const { soundPacks, coverImages, isLoading: packsLoading } = useSoundPacks();
    const [checkoutLoading, setCheckoutLoading] = useState(false);

    const isLoading = userLoading || packsLoading;
    const isSubscribed = userData?.subscription?.status === 'active';

    const cartPacks = (userData?.cartItems ?? [])
        .map((packId) => soundPacks.find((p) => p.id === packId))
        .filter((p): p is DatabaseSoundPack => Boolean(p));

    // Packs still worth showing as an upsell: public, buyable, not already in the
    // cart, not already owned (ownedSoundPacks holds Stripe price IDs). Recomputed
    // on every userData change, so a pack drops out the moment it's added.
    const cartIdSet = new Set(userData?.cartItems ?? []);
    const ownedPriceIdSet = new Set(userData?.ownedSoundPacks ?? []);
    const browsePacks = soundPacks.filter(
        (pack) =>
            pack.public &&
            !!pack.id &&
            !!pack.priceId &&
            !cartIdSet.has(pack.id) &&
            !ownedPriceIdSet.has(pack.priceId),
    );

    const subtotal = cartPacks.reduce((sum, pack) => {
        const price = pack.price ? parseFloat(pack.price) : 0;
        return sum + (isNaN(price) ? 0 : price);
    }, 0);
    const total = formatUSD(subtotal);

    const handleRemove = async (packId: string) => {
        if (!user) return;
        await DatabaseUserModel.removeFromCart(user.uid, packId);
    };

    const handleCheckout = async () => {
        const priceIds = cartPacks
            .map((pack) => pack.priceId)
            .filter((id): id is string => Boolean(id));

        if (priceIds.length === 0) return;

        setCheckoutLoading(true);
        try {
            const sessionId = await createStripeCartCheckoutSession(priceIds);
            const stripe = await getStripe();
            if (stripe) {
                const { error } = await stripe.redirectToCheckout({ sessionId });
                if (error) {
                    console.error('Stripe redirect error:', error);
                    setCheckoutLoading(false);
                }
            }
        } catch (error) {
            console.error('Error starting checkout:', error);
            setCheckoutLoading(false);
        }
    };

    const header = (label: ReactNode) => (
        <div className="flex items-baseline gap-2">
            <h1 className="text-r-title font-extrabold tracking-[-0.02em] text-ink-secondary">Cart</h1>
            {label}
        </div>
    );

    if (isLoading) {
        return (
            <div className="mx-auto max-w-[620px]">
                {header(null)}
                <p className="mt-8 text-r-body font-semibold text-subtle">Loading…</p>
            </div>
        );
    }

    const isEmpty = cartPacks.length === 0;

    return (
        <div className="mx-auto max-w-[620px]">
            {header(
                <span className="text-r-title font-extrabold tracking-[-0.02em] text-ghost-ink">
                    [{cartPacks.length}]
                </span>,
            )}

            {isEmpty ? (
                <EmptyCart className="mt-10" />
            ) : (
                <section className="mt-8 flex flex-col">
                    <div className="flex flex-col gap-5.5">
                        {cartPacks.map((pack) => (
                            <CartItemRow
                                key={pack.id}
                                pack={pack}
                                coverUrl={pack.id ? coverImages[pack.id] : undefined}
                                onRemove={() => pack.id && handleRemove(pack.id)}
                            />
                        ))}
                    </div>

                    <Divider tone="line" className="mt-6.5" />

                    <div className="flex items-baseline justify-between py-4">
                        <span className="text-r-body font-bold text-ink">Subtotal</span>
                        <span className="text-r-body font-bold text-ink">{total}</span>
                    </div>

                    <div className="flex flex-wrap gap-2.5">
                        {!isSubscribed && (
                            <Button
                                variant="secondary"
                                size="md"
                                className="flex-1 basis-[200px]"
                                onClick={() => navigate('/subscribe')}
                            >
                                Free with All-Access
                            </Button>
                        )}
                        <Button
                            variant="primary"
                            size="md"
                            className="flex-1 basis-[200px]"
                            onClick={handleCheckout}
                            disabled={checkoutLoading}
                        >
                            {checkoutLoading ? 'One moment…' : 'Check out'}
                        </Button>
                    </div>
                </section>
            )}

            {browsePacks.length > 0 && (
                <>
                    <Divider tone="line" className="mt-18" />
                    <PackScroller
                        title={
                            <>
                                Browse <span className="text-ghost-ink">Packs</span>
                            </>
                        }
                        packs={browsePacks}
                        coverImages={coverImages}
                        packHref={() => LANDING_PACKS_URL}
                        overlayAction={(pack) => (pack.id ? <CartToggleButton packId={pack.id} /> : null)}
                        action={
                            <a href={LANDING_PACKS_URL} className="text-r-cap font-semibold text-ink">
                                See all
                            </a>
                        }
                        className="mt-11"
                    />
                </>
            )}

            {!isSubscribed && <AllAccessBanner />}
        </div>
    );
}

export default CartPage;

// ============================ Sub-components ============================

function CartItemRow({
    pack,
    coverUrl,
    onRemove,
}: {
    pack: DatabaseSoundPack;
    coverUrl?: string;
    onRemove: () => void;
}) {
    return (
        <div className="flex items-center gap-4">
            <PackArtwork
                src={coverUrl}
                alt={`${pack.name} cover`}
                border="subtle"
                className="h-[92px] w-[92px]"
            />
            <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
                <span className="text-[10px] text-subtle">Organic Electronics</span>
                <span className="truncate text-r-item font-bold text-ink">
                    {pack.name}
                    {pack.secondaryTitle && <span className="font-bold text-subtle"> {pack.secondaryTitle}</span>}
                </span>
                <span className="text-r-cap text-subtle">{DatabaseSoundPackModel.formatPrice(pack.price)}</span>
            </div>
            <button
                onClick={onRemove}
                aria-label={`Remove ${pack.name}`}
                className="flex-none transition-opacity hover:opacity-70"
            >
                <PackRemoveIcon />
            </button>
        </div>
    );
}

function EmptyCart({ className = '' }: { className?: string }) {
    return (
        <section className={`flex flex-col items-center text-center ${className}`}>
            <DotHexagon dot={11} />
            <Headline stacked lead="Your Cart is" trail="Empty" className="mt-6" />
            <Button variant="primary" size="md" href={LANDING_PACKS_URL} className="mt-6 gap-1.5">
                <span className="text-[16px] font-medium leading-none">+</span>
                Add Packs
            </Button>
        </section>
    );
}
